import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { PhoneCall, Check, MapPin, ArrowRight, Star, Wifi, Shield, Zap } from 'lucide-react';

const providers = [
  { name: 'Telekom', color: 'bg-pink-50 border-pink-200', badge: 'Beste Netzqualität' },
  { name: 'Vodafone', color: 'bg-red-50 border-red-200', badge: 'Große Auswahl' },
  { name: 'O2', color: 'bg-blue-50 border-blue-200', badge: 'Günstige Preise' },
  { name: '', color: 'bg-orange-50 border-orange-200', badge: 'Viele Inklusivleistungen' },
];

const benefits = [
  { icon: <MapPin className="w-5 h-5 text-blue-600" />, title: 'Lokal in Völklingen', text: 'Persönliche Beratung vor Ort – Bismarck Straße 31, 66333 Völklingen' },
  { icon: <Shield className="w-5 h-5 text-blue-600" />, title: 'Alle Anbieter im Vergleich', text: 'Telekom, Vodafone, O2 – wir finden den besten Tarif für Sie' },
  { icon: <Star className="w-5 h-5 text-blue-600" />, title: 'Kostenlose Beratung', text: 'Unverbindlich und ohne versteckte Kosten – wir verdienen nur bei Abschluss' },
  { icon: <Zap className="w-5 h-5 text-blue-600" />, title: 'Schnelle Abwicklung', text: 'Vom Vergleich bis zum Vertragsabschluss – alles aus einer Hand' },
];

const faqs = [
  {
    q: 'Wo kann ich in Völklingen einen Handyvertrag abschließen?',
    a: 'Bei Easy Vertrag in der Bismarck Straße 31, 66333 Völklingen. Wir beraten Sie persönlich und helfen Ihnen, den besten Handyvertrag von Telekom, Vodafone, O2 oder  zu finden.',
  },
  {
    q: 'Welcher Handyvertrag ist in Völklingen am günstigsten?',
    a: 'Das hängt von Ihrem Datenvolumen und Netzwunsch ab. In Völklingen haben alle großen Netze gute Abdeckung. Wir vergleichen alle Anbieter kostenlos für Sie und empfehlen den besten Tarif.',
  },
  {
    q: 'Kann ich in Völklingen auch ein Smartphone mit Vertrag kaufen?',
    a: 'Ja! Wir bieten Smartphones (iPhone, Samsung, Google Pixel u.v.m.) in Kombination mit Handyverträgen aller großen Anbieter an. Oft ist das günstiger als ein separater Kauf.',
  },
  {
    q: 'Gibt es auch Tarife ohne Laufzeit in Völklingen?',
    a: 'Ja, wir vergleichen sowohl 24-Monats-Verträge als auch Prepaid-Karten und Laufzeitverträge ohne Bindung – ganz nach Ihrem Bedarf.',
  },
];

const HandyvertragVoelklingenPage: React.FC = () => {
  return (
    <div className="pt-24 pb-16">
      <Helmet>
        <title>Handyvertrag Völklingen - Alle Anbieter vergleichen | Easy Vertrag</title>
        <meta name="description" content="Handyvertrag in Völklingen abschließen - persönliche Beratung vor Ort. Telekom, Vodafone, O2 vergleichen. Bismarck Straße 31, 66333 Völklingen. Kostenlos beraten lassen!" />
        <meta name="keywords" content="Handyvertrag Völklingen, Mobilfunkvertrag Völklingen, Handytarif Saarland, Telekom Völklingen, Vodafone Völklingen, O2 Völklingen, Smartphone Vertrag Saarland" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://easy4vertrag.de/handyvertrag-voelklingen" />
        <meta property="og:title" content="Handyvertrag Völklingen - Alle Anbieter vergleichen | Easy Vertrag" />
        <meta property="og:description" content="Handyvertrag in Völklingen abschließen - persönliche Beratung vor Ort. Telekom, Vodafone, O2 kostenlos vergleichen." />
        <meta property="og:url" content="https://easy4vertrag.de/handyvertrag-voelklingen" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              ${faqs.map(f => `{
                "@type": "Question",
                "name": "${f.q}",
                "acceptedAnswer": {"@type": "Answer", "text": "${f.a}"}
              }`).join(',')}
            ]
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://easy4vertrag.de/"},
              {"@type": "ListItem", "position": 2, "name": "Handyvertrag Völklingen", "item": "https://easy4vertrag.de/handyvertrag-voelklingen"}
            ]
          }
        `}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-700 to-blue-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white bg-opacity-15 rounded-full px-4 py-2 text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              Bismarck Straße 31 · 66333 Völklingen · Saarland
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-5 leading-tight">
              Handyvertrag in Völklingen abschließen
            </h1>
            <p className="text-xl text-blue-100 mb-4">
              Ihr lokaler Experte für Mobilfunkverträge im Saarland. Telekom, Vodafone, O2 kostenlos vergleichen – persönlich vor Ort oder online.
            </p>
            <p className="text-blue-200 mb-8 text-base">
              Mo–Fr 10–20 Uhr · Sa 10–18 Uhr · +49 179 7473491
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/angebote/tarif"
                className="inline-flex items-center justify-center px-7 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-colors"
              >
                Tarife jetzt vergleichen <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <a
                href="https://wa.me/491797473491"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-7 py-3 border-2 border-white bg-transparent text-white font-semibold rounded-lg hover:bg-white hover:text-blue-700 transition-colors"
              >
                WhatsApp Beratung
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-3">
              Warum Handyvertrag in Völklingen bei Easy Vertrag?
            </h2>
            <p className="text-gray-500 text-center mb-10 max-w-2xl mx-auto">
              Als lokaler Anbieter in Völklingen kennen wir die Netzversorgung im Saarland genau und finden den Tarif, der wirklich zu Ihnen passt.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-4 p-5 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    {b.icon}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-1">{b.title}</h3>
                    <p className="text-gray-600 text-sm">{b.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Providers */}
      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-3">
              Handyverträge aller Anbieter in Völklingen
            </h2>
            <p className="text-gray-500 text-center mb-10">
              Wir vermitteln Verträge von allen großen Netzbetreibern – neutral und unabhängig.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {providers.map((p, i) => (
                <div key={i} className={`border-2 rounded-xl p-5 text-center ${p.color}`}>
                  <p className="text-xl font-bold text-gray-800 mb-2">{p.name}</p>
                  <span className="text-xs font-medium text-gray-500 bg-white px-2 py-1 rounded-full border">{p.badge}</span>
                </div>
              ))}
            </div>
            <div className="bg-blue-600 text-white rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-3">Kostenloser Tarifvergleich für Völklingen</h3>
              <p className="text-blue-100 mb-6 max-w-lg mx-auto">
                Unser Rechner zeigt Ihnen alle verfügbaren Tarife mit dem besten Preis-Leistungs-Verhältnis für Ihren Standort im Saarland.
              </p>
              <Link
                to="/angebote/tarif"
                className="inline-flex items-center px-8 py-3 bg-white text-blue-600 font-bold rounded-lg hover:bg-blue-50 transition-colors"
              >
                Jetzt Tarife vergleichen <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-10">
              Handyverträge in Völklingen – unsere Leistungen
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: <PhoneCall className="w-6 h-6 text-blue-600" />, title: 'SIM-only Tarife', text: 'Günstige Tarife ohne Gerät – für alle Netze verfügbar.' },
                { icon: <Wifi className="w-6 h-6 text-blue-600" />, title: 'Smartphone mit Vertrag', text: 'iPhone, Samsung & Co. kombiniert mit dem besten Tarif.' },
                { icon: <Shield className="w-6 h-6 text-blue-600" />, title: 'Prepaid & Allnet-Flat', text: 'Flexible Optionen ohne Laufzeit oder mit günstigen Monatsbeiträgen.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-6 text-center border border-gray-100">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {item.icon}
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-10">
              Häufige Fragen zu Handyverträgen in Völklingen
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-800 mb-2 text-base">{faq.q}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Local CTA */}
      <section className="py-14 bg-blue-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Jetzt Handyvertrag in Völklingen abschließen</h2>
            <p className="text-blue-100 mb-2">Easy Vertrag · Bismarck Straße 31 · 66333 Völklingen</p>
            <p className="text-blue-200 text-sm mb-8">Mo–Fr 10:00–20:00 Uhr · Sa 10:00–18:00 Uhr</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+491797473491"
                className="inline-flex items-center justify-center px-7 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-colors"
              >
                <PhoneCall className="w-5 h-5 mr-2" />
                Jetzt anrufen
              </a>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center px-7 py-3 border-2 border-white bg-transparent text-white font-semibold rounded-lg hover:bg-white hover:text-blue-700 transition-colors"
              >
                Nachricht schicken <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HandyvertragVoelklingenPage;
